import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Claim, ClaimType, Product, Sku } from "../../api/models"
import Api, { ApiError } from "../../api/api"
import { redeemResellerCodeThunk } from "../../redux/redemptionsSlice"
import { AppDispatch } from "../../redux/store"
import { useSession } from "../../auth/session"
import CardImage from "../widgets/CardImage"
import SkuName from "../widgets/SkuName"
import { RESELLER_PAGES, ResellerPage } from "./resellerPages"

// The hidden page where a code handed out by a reseller (Fender) for one
// product is redeemed: the License Codes wizard (enter → review → done) for
// that list's own code shape, which the regular Redeem card would reject (no
// sku prefix, no checksum). One route per list (resellerPages.ts); not in
// the sidebar — the reseller's instructions link straight here, and a
// signed-out visitor comes back after signing in (returnTo). `?code=`
// prefills the form so the link can carry the code. A code from the
// reseller's OTHER list is refused with a pointer to its own page.

const CHAR_RE = /^[a-zA-Z0-9]$/

type Step = "enter" | "review" | "success"

const stepLabels: { key: Step; label: string }[] = [
  { key: "enter", label: "Enter" },
  { key: "review", label: "Review" },
  { key: "success", label: "Done" },
]

function filterChars(raw: string): string {
  return raw.split("").filter(ch => CHAR_RE.test(ch)).join("").toUpperCase()
}

// Splits pasted/typed text into the segments from `from` on
function fillSegments(lengths: readonly number[], segments: string[], from: number, text: string): string[] {
  const chars = filterChars(text)
  const next = [...segments]
  let pos = 0
  for (let i = from; i < lengths.length && pos < chars.length; i++) {
    next[i] = chars.slice(pos, pos + lengths[i])
    pos += lengths[i]
  }
  return next
}

export default function ResellerRedeemPage({ page }: { page: ResellerPage }) {
  const { reseller, resellerName: name, skuName, segments: SEGMENT_LENGTHS } = page
  const CODE_LENGTH = SEGMENT_LENGTHS.reduce((a, b) => a + b, 0)
  const emptySegments = () => SEGMENT_LENGTHS.map(() => "")
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const dispatch = useDispatch<AppDispatch>()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<Step>("enter")
  const [segments, setSegments] = useState<string[]>(() => {
    const prefilled = searchParams.get("code")
    return prefilled ? fillSegments(SEGMENT_LENGTHS, emptySegments(), 0, prefilled) : emptySegments()
  })
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [sku, setSku] = useState<Sku | undefined>(undefined)
  const [claims, setClaims] = useState<Claim[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const stepIndex = stepLabels.findIndex(s => s.key === step)
  const fullCode = segments.join("-")
  const isFull = segments.every((s, i) => s.length === SEGMENT_LENGTHS[i])

  const focusSegment = useCallback((index: number) => {
    inputRefs.current[index]?.focus()
  }, [])

  // on arrival: the first incomplete segment takes the caret (the last one
  // when a ?code= link filled everything in)
  useEffect(() => {
    const firstEmpty = segments.findIndex((s, i) => s.length < SEGMENT_LENGTHS[i])
    focusSegment(firstEmpty === -1 ? SEGMENT_LENGTHS.length - 1 : firstEmpty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (index: number, raw: string) => {
    const filtered = filterChars(raw).slice(0, SEGMENT_LENGTHS[index])
    setSegments(prev => {
      const next = [...prev]
      next[index] = filtered
      return next
    })
    setError("")
    if (filtered.length === SEGMENT_LENGTHS[index] && index < SEGMENT_LENGTHS.length - 1)
      focusSegment(index + 1)
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && segments[index] === "" && index > 0) {
      e.preventDefault()
      focusSegment(index - 1)
    }
    if (e.key === "ArrowLeft" && e.currentTarget.selectionStart === 0 && index > 0) {
      e.preventDefault()
      focusSegment(index - 1)
    }
    if (e.key === "ArrowRight" && e.currentTarget.selectionStart === segments[index].length && index < SEGMENT_LENGTHS.length - 1) {
      e.preventDefault()
      focusSegment(index + 1)
    }
    if (e.key === "Enter")
      handleSubmit()
  }

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault()
    const next = fillSegments(SEGMENT_LENGTHS, segments, index, e.clipboardData.getData("text"))
    setSegments(next)
    setError("")
    const lastFilled = next.reduce((last, s, i) => (i >= index && s.length > 0 ? i : last), index)
    const target = next[lastFilled].length === SEGMENT_LENGTHS[lastFilled] && lastFilled < SEGMENT_LENGTHS.length - 1
      ? lastFilled + 1
      : lastFilled
    setTimeout(() => focusSegment(target), 0)
  }

  const messageFor = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 404)
      return `That doesn't look like a valid ${name} code. Please check it and try again.`
    return (err as Error)?.message || fallback
  }

  const handleSubmit = async () => {
    if (!isFull) {
      setError(`Please enter the complete ${CODE_LENGTH}-character code.`)
      return
    }
    setLoading(true)
    setError("")
    try {
      const result = await Api.getResellerCode({ reseller, code: fullCode, accessToken })
      if (result.sku.name !== skuName) {
        // the reseller's other list — same table, its own page and code shape
        const other = RESELLER_PAGES.find(p => p.reseller === reseller && p.skuName === result.sku.name)
        setError(`This is a ${result.sku.name} code, not a ${skuName} code.${other ? ` Redeem it at ${window.location.origin}${other.path}` : ""}`)
        return
      }
      setSku(result.sku)
      setClaims(result.claims)
      setProducts(result.products)
      setStep("review")
    } catch (err) {
      setError(messageFor(err, "Failed to look up the code. Please try again."))
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async () => {
    setLoading(true)
    setError("")
    try {
      await dispatch(redeemResellerCodeThunk({ reseller, code: fullCode, accessToken })).unwrap()
      setStep("success")
    } catch (err) {
      setError(messageFor(err, "Failed to redeem the code. Please try again."))
    } finally {
      setLoading(false)
    }
  }

  const handleRedeemAnother = () => {
    setStep("enter")
    setSegments(emptySegments())
    setError("")
    setSku(undefined)
    setClaims([])
    setProducts([])
    setTimeout(() => focusSegment(0), 0)
  }

  const licensedProducts = claims
    .filter(c => c.type === ClaimType.License)
    .map(c => products.find(p => p.id === c.productId)?.name ?? c.productId)
  const placeholders = SEGMENT_LENGTHS.map(n => "X".repeat(n))

  return (
    <div className="page">
      <h1 className="page-title">{skuName} for {name}</h1>

      <div className="card redeem-card">
        <h2 className="card-title">Redeem your {skuName} code</h2>
        <p className="card-detail reseller-intro">Enter the {skuName} license code you received from {name} to add it to your account.</p>
        <div className="redeem-card-body">
          <div className="wizard-steps">
            {stepLabels.map((s, i) => (
              <span key={s.key} style={{ display: "contents" }}>
                {i > 0 && (
                  <div className={`wizard-line${i <= stepIndex ? " wizard-line-done" : ""}`} />
                )}
                <div className={`wizard-node${i === stepIndex ? (i === stepLabels.length - 1 ? " wizard-node-done" : " wizard-node-active") : i < stepIndex ? " wizard-node-done" : ""}`}>
                  <div className="wizard-dot" />
                  <span className="wizard-label">{s.label}</span>
                </div>
              </span>
            ))}
          </div>

          {step === "enter" && (
            <div>
              <div className="code-input-group">
                {segments.map((seg, i) => (
                  <span key={i} style={{ display: "flex", alignItems: "center" }}>
                    {i > 0 && <span className="code-dash">-</span>}
                    <input
                      ref={el => { inputRefs.current[i] = el }}
                      className={`code-segment${error ? " code-segment-error" : ""}`}
                      style={{ width: `${SEGMENT_LENGTHS[i] * 1.4}ch` }}
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      maxLength={SEGMENT_LENGTHS[i]}
                      placeholder={placeholders[i]}
                      value={seg}
                      onChange={e => handleChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      onPaste={e => handlePaste(i, e)}
                    />
                  </span>
                ))}
              </div>
              <p className="redeem-error" style={{ visibility: error ? "visible" : "hidden" }}>{error || " "}</p>
              <div className="redeem-actions">
                {segments.some(s => s.length > 0) && (
                  <button className="btn-downgrade" onClick={() => { setSegments(emptySegments()); setError(""); setTimeout(() => focusSegment(0), 0) }}>Clear</button>
                )}
                <button onClick={handleSubmit} disabled={!isFull || loading}>{loading ? "Loading…" : "Next"}</button>
              </div>
            </div>
          )}

          {step === "review" && sku && (
            <div>
              <h3 className="redeem-bundle-title">This code unlocks</h3>
              <div className="reseller-review">
                <div className="reseller-review-img">
                  <CardImage imageUrl={sku.imageUrl} availableWidths={sku.availableWidths} alt={sku.name} />
                </div>
                <div className="redeem-summary">
                  <div className="redeem-summary-row">
                    <span>Product</span>
                    <span className="redeem-summary-value"><SkuName sku={sku} variant="section" /></span>
                  </div>
                  {licensedProducts.map(productName => (
                    <div className="redeem-summary-row" key={productName}>
                      <span>License</span>
                      <span className="redeem-summary-value">{productName}</span>
                    </div>
                  ))}
                  {claims.filter(c => c.type === ClaimType.Topup).map(c => (
                    <div className="redeem-summary-row" key={c.id}>
                      <span>Credits</span>
                      <span className="redeem-summary-value">+{((c.amount ?? 0) / 1e9).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="redeem-error" style={{ visibility: error ? "visible" : "hidden" }}>{error || " "}</p>
              <div className="redeem-actions">
                <button className="btn-downgrade" onClick={handleRedeemAnother} disabled={loading}>Back</button>
                <button onClick={handleConfirm} disabled={loading}>{loading ? "Redeeming…" : "Redeem"}</button>
              </div>
            </div>
          )}

          {step === "success" && (
            <div className="redeem-success">
              <span className="redeem-success-icon">&#10003;</span>
              <h3 className="redeem-bundle-title">{sku?.name ?? "Your software"} is now on your account</h3>
              <p>Download the installer and sign in from the plug-in to activate it. We've also emailed you the download links.</p>
              {licensedProducts.length > 0 && (
                <p>Licenses: {licensedProducts.join(", ")}</p>
              )}
              <div className="redeem-success-links">
                <Link to="/installers">Download installers</Link>
                <Link to="/licenses">View Licenses</Link>
              </div>
              <div className="redeem-actions">
                <button onClick={handleRedeemAnother}>Redeem another code</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
